import { faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import useMediaQuery from '@mui/system/useMediaQuery';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';
import {
  ConfirmDialog,
  useConfirmDialogComponent,
} from 'src/components/ConfirmDialog';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Meta } from 'src/components/Meta';
import { SearchParamsPagination } from 'src/components/Pagination';
import { SearchParamSearchTextField } from 'src/components/SearchTextField';
import { SectionToolbar } from 'src/components/SectionToolbar';
import { DataTable } from 'src/components/Table/DataTable';
import { Theme } from 'src/components/muiTheme/types';
import {
  deleteLeaderboard,
  fetchLeaderboards,
  fetchLeaderboardsQueryKey,
} from 'src/models/leaderboard/leaderboard';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';
import { ContainerBreakout } from 'src/shared/AppFrame/ContainerBreakout';
import { useListSearchParams } from 'src/utils/useListSearchParams';

export const LeaderboardsPage = () => {
  const navigate = useNavigate();
  const { params, updateParam } = useListSearchParams();
  const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const itemsPerPage = params.perPage || 20;
  const { t } = useTranslation('translation', { keyPrefix: 'leaderboard' });
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });

  const queryParams = {
    page: params.page,
    perPage: itemsPerPage,
    search: params.search,
    sortBy: params.sortBy,
    sortDir: params.sortDir,
  };

  const { data, isLoading } = useQuery({
    queryKey: fetchLeaderboardsQueryKey(queryParams),
    queryFn: () => fetchLeaderboards(queryParams),
  });

  const [confirmDialogProps, confirm] = useConfirmDialogComponent();

  const columns = [
    { id: 'name', label: t('table.name'), sortable: true },
    { id: 'description', label: t('table.description') },
    { id: 'measurement', label: t('table.measurement') },
    {
      id: 'createdAt',
      label: t('table.created'),
      sortable: true,
      format: (value: string) => new Date(value).toLocaleDateString(),
    },
  ];

  const handleDelete = async (row) => {
    const confirmed = await confirm({
      title: t('table.deleteLeaderboard'),
      message: tCommon('messages.confirmDelete', { name: row.name }),
      confirmText: tCommon('buttons.delete'),
    });

    if (confirmed) {
      await deleteLeaderboard(row.id);
      queryClient.invalidateQueries({ queryKey: ['leaderboards'] });
    }
  };

  return (
    <>
      <Meta title={t('title')} />
      <AppFrameConfig
        id="leaderboards"
        containerMaxWidth="xl"
        title={t('title')}
        actions={
          <>
            {smDown ? (
              <IconButton onClick={() => navigate('/leaderboards/new')}>
                <Icon icon={faPlus} />
              </IconButton>
            ) : (
              <Button
                variant="prominent"
                startIcon={<Icon icon={faPlus} />}
                component={Link}
                to="/leaderboards/new"
              >
                {t('actions.newLeaderboard')}
              </Button>
            )}
          </>
        }
      />
      <SectionToolbar sx={{ pb: 3, pt: 0, px: 0 }}>
        <SearchParamSearchTextField
          params={params}
          updateParam={updateParam}
          sx={{ width: smDown ? '100%' : '280px' }}
        />
      </SectionToolbar>
      <ContainerBreakout smDown>
        <Card sx={{ mb: 6 }} square={smDown}>
          <DataTable
            fixedActions
            columns={columns}
            rows={data?.leaderboard || []}
            loading={isLoading}
            sortBy={params.sortBy}
            sortDir={params.sortDir}
            setSortBy={(sortBy) => updateParam('sortBy', sortBy)}
            setSortDir={(sortDir) => updateParam('sortDir', sortDir)}
            rowLinkTo={(row) => `/leaderboards/${row.id}/edit`}
            rowActions={[
              {
                icon: faTrash,
                label: tCommon('actions.delete'),
                onClick: handleDelete,
              },
            ]}
            noResultsText={tCommon('noResults', {
              items: t('title').toLowerCase(),
            })}
          />
          <SearchParamsPagination
            total={data?.meta.total || 0}
            itemsPerPage={itemsPerPage}
            params={params}
            updateParam={updateParam}
          />
        </Card>
      </ContainerBreakout>
      <ConfirmDialog {...confirmDialogProps} />
    </>
  );
};
